import React from 'react';
import { Box, Typography, Chip, Paper, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useParams } from 'react-router-dom';
import {
    sortByUrgency,
    sortByDueDate,
    sortByCreationDate,
    sortByDeferredCost,
} from './Buttons.jsx';
import QuickActionButton from './QuickActionButton';
import { getStatusChipProps } from './constants';
import { useDeficiencies } from './DeficiencyContext';
import { useNotifications } from '../../../NotificationSystem';
import { usePermissions } from '../../../hooks/usePermissions';

/**
 * Custom StatusChip component that displays both status and pending description
 *
 * @param {Object} props - Component props
 * @param {string} props.status - The deficiency status
 * @returns {React.ReactElement} Enhanced status chip with pending indicator
 */
const StatusChip = ({ status, role, showDescription = false }) => {
    const theme = useTheme();
    const chipProps = getStatusChipProps(status, role);

    // Create the combined label with status and pending description
    const chipLabel = (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    lineHeight: 1.2,
                    letterSpacing: '0.01em',
                }}
            >
                {chipProps.label}
            </Typography>
            {showDescription && chipProps.pendingDescription && (
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '0.625rem',
                        lineHeight: 1.1,
                        opacity: 0.85,
                        width: '100%',
                        textAlign: 'center',
                        mt: 0.1,
                    }}
                >
                    {chipProps.pendingDescription}
                </Typography>
            )}
        </Box>
    );

    return (
        <Chip
            label={chipLabel}
            size="small"
            sx={{
                ...chipProps.sx,
                height: 'auto',
                py: 0.3,
                '& .MuiChip-label': {
                    padding: '2px 10px',
                    display: 'block',
                    width: '100%',
                },
                borderRadius: '16px',
                transition: 'all 0.2s ease',
                fontFamily: theme.typography.fontFamily,
            }}
        />
    );
};

/**
 * Component for displaying a list of deficiencies, with grouping and batch selection capabilities
 *
 * @param {Object} props - Component props
 * @param {Array} props.deficiencies - Array of deficiency objects to display
 * @param {string} props.sortType - How to sort deficiencies ('urgency', 'dueDate', 'dateCreated', 'deferredCost')
 * @param {boolean} props.isBatchMode - Whether batch selection mode is active
 * @param {Set} props.selectedItems - Set of selected item IDs for batch operations
 * @param {Function} props.onSelectionChange - Callback when selection changes in batch mode
 * @param {Function} props.onStatusChange - Callback when a deficiency status is changed
 * @param {Function} props.onAssign - Callback when a deficiency is assigned to someone
 * @param {Function} props.onDeficiencySelect - Callback when a deficiency is selected for viewing details
 * @returns {React.ReactElement} Deficiency list UI
 */
const DeficiencyList = ({
    deficiencies,
    sortType,
    isBatchMode,
    selectedItems,
    onSelectionChange,
    onStatusChange,
    onAssign,
    onDeficiencySelect,
    onTerminalRejection,
    onWorkRejection,
}) => {
    const theme = useTheme();
    const { state, getImageUrl } = useDeficiencies();
    const { businesses } = state;
    const { notifications } = useNotifications();

    // Get projectId from URL params
    const { projectId } = useParams();

    // Get role from usePermissions hook to check if user can see quick actions
    const { getProjectRole } = usePermissions();
    const role = getProjectRole(projectId);

    // QuickActionButton is only for internal managers
    const isInternalManager = ['INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_ADMIN'].includes(role);

    // Helper function to get the assigned business name
    const getAssignedBusinessName = (deficiency) => {
        if (!deficiency.assignedToBusinessId) return 'Unassigned';

        // Prefer the name stored directly on the deficiency DTO
        if (deficiency.assignedToBusinessName) return deficiency.assignedToBusinessName;

        // Fallback: look up from the loaded businesses list
        const assignedBusiness = businesses.find(
            (business) => business.businessId === deficiency.assignedToBusinessId
        );
        if (assignedBusiness) {
            return assignedBusiness.businessName;
        }

        return 'Unknown Business';
    };

    //This is the function that handles the selection of items for batch operations
    const handleSelect = (itemId) => {
        const newSelected = new Set(selectedItems);
        if (newSelected.has(itemId)) {
            newSelected.delete(itemId);
        } else {
            newSelected.add(itemId);
        }
        onSelectionChange(newSelected);
    };

    // Helper function to get consistent ID
    const getDeficiencyId = (deficiency) => {
        return deficiency.deficiencyId;
    };

    // Helper function to get notifications for a specific deficiency (for urgency ranking - use resolved flag)
    const getDeficiencyNotifications = (deficiencyId) => {
        if (!notifications || !Array.isArray(notifications)) {
            return [];
        }
        return notifications.filter(
            (notification) =>
                notification.entityType === 'DEFICIENCY' &&
                notification.entityId === deficiencyId &&
                !notification.resolved
        );
    };

    // Helper function to get general notifications for bell icon (use viewed flag)
    const getGeneralNotifications = (deficiencyId) => {
        if (!notifications || !Array.isArray(notifications)) {
            return [];
        }
        // Include ALL unviewed notifications for the bell, including critical ones
        return notifications.filter(
            (notification) =>
                notification.entityType === 'DEFICIENCY' &&
                notification.entityId === deficiencyId &&
                !notification.viewed
        );
    };

    // Helper function to determine if a deficiency is high priority
    const isHighPriority = (deficiency) => {
        const deficiencyNotifications = getDeficiencyNotifications(deficiency.deficiencyId);
        // Due-date state is the primary signal; notifications are secondary.
        const dueState = deficiency?.dueDateState;
        if (dueState === 'OVERDUE') return true;

        // Keep existing high-priority notification signals (e.g., escalations/rejections) as secondary.
        return deficiencyNotifications.some(
            (notification) =>
                notification.eventKey === 'INTERNAL_ESCALATION' ||
                notification.eventKey === 'EXTERNAL_ESCALATION' ||
                notification.eventKey === 'INTERNAL_REJECTION' ||
                notification.eventKey === 'EXTERNAL_REJECTION'
        );
    };

    // Helper function to get priority type and styling for enhanced visual indicators
    const getPriorityInfo = (deficiency) => {
        if (!isHighPriority(deficiency)) return null;

        const criticalNotifications = getDeficiencyNotifications(deficiency.deficiencyId);
        const dueState = deficiency?.dueDateState;

        const hasEscalation = criticalNotifications.some(
            (notification) =>
                notification.eventKey === 'INTERNAL_ESCALATION' ||
                notification.eventKey === 'EXTERNAL_ESCALATION'
        );

        const hasReminder = criticalNotifications.some(
            (notification) => notification.eventKey === 'DUEDATE_REMINDER'
        );

        const hasRejection = criticalNotifications.some(
            (notification) =>
                notification.eventKey === 'INTERNAL_REJECTION' ||
                notification.eventKey === 'EXTERNAL_REJECTION'
        );

        // Overdue gets highest priority (red)
        if (dueState === 'OVERDUE' || hasEscalation || hasRejection) {
            return {
                type: 'overdue',
                backgroundColor: theme.palette.error.main + '08', // 8% opacity
                borderColor: theme.palette.error.main + '30', // 30% opacity
                borderWidth: '2px',
            };
        }

        // Reminders get warning priority (yellow)
        if (dueState === 'DUE_SOON' || hasReminder) {
            return {
                type: 'reminder',
                backgroundColor: theme.palette.warning.main + '08',
                borderColor: theme.palette.warning.main + '30',
                borderWidth: '2px',
            };
        }

        return null;
    };

    // Notification banner component with icon and descriptive text
    // - Due Soon / Overdue: derived from backend-computed dueDateState (source of truth for UI)
    // - New Updates: derived from unviewed notifications (view-driven)
    const NotificationBanner = ({ deficiencyId, dueDateState }) => {
        const generalNotifications = getGeneralNotifications(deficiencyId); // Use viewed flag for bell

        const hasOverdue = dueDateState === 'OVERDUE';
        const hasReminder = dueDateState === 'DUE_SOON';
        const hasBellNotifications = generalNotifications.length > 0;

        // If no notifications at all, return null
        if (!hasOverdue && !hasReminder && !hasBellNotifications) {
            return null;
        }

        // Create array of banners to show (max 2)
        const banners = [];

        // Add ONE critical banner (overdue takes precedence over reminder)
        if (hasOverdue) {
            banners.push({
                id: 'critical',
                icon: '🚨',
                text: 'Overdue',
                bgColor: theme.palette.error.main,
                textColor: 'white',
            });
        } else if (hasReminder) {
            banners.push({
                id: 'warning',
                icon: '⚠️',
                text: 'Due Soon',
                bgColor: theme.palette.warning.main,
                textColor: 'white',
            });
        }

        // Add updates banner if there are general notifications
        if (hasBellNotifications) {
            banners.push({
                id: 'updates',
                icon: '🔔',
                text: 'New Updates',
                bgColor: theme.palette.info.main,
                textColor: 'white',
            });
        }

        // Determine layout: horizontal if 2 banners, vertical if 1
        const isHorizontal = banners.length === 2;

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isHorizontal ? 'row' : 'column',
                    gap: isHorizontal ? 0.5 : 0.25,
                    alignItems: 'flex-end',
                }}
            >
                {banners.map((banner) => (
                    <Box
                        key={banner.id}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.25,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 0.75,
                            backgroundColor: banner.bgColor,
                            color: banner.textColor,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                            minHeight: '18px',
                            // Make banners slightly smaller when horizontal
                            ...(isHorizontal && {
                                px: 0.5,
                                fontSize: '0.6rem',
                            }),
                        }}
                    >
                        <Box sx={{ fontSize: isHorizontal ? '9px' : '10px', lineHeight: 1 }}>
                            {banner.icon}
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: isHorizontal ? '0.6rem' : '0.65rem',
                                fontWeight: 600,
                                color: 'inherit',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                lineHeight: 1,
                            }}
                        >
                            {banner.text}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    //This is the function that groups the deficiencies by location, trade, or status
    const getGroupedDeficiencies = () => {
        let groupedData = {};

        switch (sortType) {
            case 'dueDate':
                groupedData = sortByDueDate(deficiencies);
                break;
            case 'dateCreated':
                groupedData = sortByCreationDate(deficiencies);
                break;
            case 'deferredCost':
                groupedData = sortByDeferredCost(deficiencies);
                break;
            case 'urgency':
            default:
                groupedData = sortByUrgency(deficiencies, getDeficiencyNotifications);
                break;
        }

        // Build sorted categories
        const categories = Object.entries(groupedData).map(([name, items]) => ({
            name,
            items,
        }));

        return categories;
    };

    //This is the function that gets the grouped deficiencies
    const categories = getGroupedDeficiencies();

    // Handle clicking on a deficiency item
    const handleDeficiencyClick = (deficiency, event) => {
        // Check if the click came from within a QuickActionButton
        if (event.target.closest('.quick-action-button')) {
            // Don't select the deficiency
            return;
        }

        // If we're in batch mode, handle selection
        if (isBatchMode) {
            handleSelect(getDeficiencyId(deficiency));
        }
        // Otherwise, show the deficiency details if not clicking the quick action button
        else if (onDeficiencySelect && event.target.closest('.quick-action-button') === null) {
            onDeficiencySelect(deficiency);
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '60vh',
                // Add bottom padding when in batch mode to prevent the batch action bar from covering the last item
                pb: isBatchMode ? '80px' : 0,
            }}
        >
            {categories.map((category, index) => (
                <Box
                    key={index}
                    sx={{
                        mb: 3,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: { xs: 0.5, sm: 1 },
                    }}
                >
                    {/* Table */}
                    <Paper
                        elevation={0}
                        sx={{
                            flex: 1,
                            borderRadius: { xs: '12px', sm: '12px' },
                            border: `1px solid ${theme.palette.grey[100]}`,
                            overflow: 'hidden',
                            boxShadow: { xs: 'none', sm: '0 1px 3px rgba(0,0,0,0.05)' },
                        }}
                    >
                        {/* Category Header */}
                        <Box
                            sx={{
                                p: { xs: 2, sm: 2.5 },
                                borderBottom: `1px solid ${theme.palette.grey[200]}`,
                                backgroundColor: theme.palette.grey[50],
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {category.name}
                            </Typography>
                        </Box>

                        {/* Table Headers - Only visible on tablet/desktop */}
                        <Grid
                            container
                            sx={{
                                px: { xs: 2.5, sm: 2.5 },
                                py: { xs: 1.5, sm: 2 },
                                borderBottom: `1px solid ${theme.palette.grey[200]}`,
                                backgroundColor: theme.palette.grey[50],
                                display: { xs: 'none', sm: 'flex' },
                            }}
                        >
                            <Grid size={{ xs: 12, sm: 2, md: 2 }} key="header-before">
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    Before
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 2, md: 2 }} key="header-after">
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    After
                                </Typography>
                            </Grid>
                            <Grid
                                size={{ xs: 12, sm: 3.5, md: 3.5 }}
                                sx={{ textAlign: 'left' }}
                                key="header-title"
                            >
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    Title
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 1.5, md: 1.5 }} key="header-location">
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    Room
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 1.5, md: 1.5 }} key="header-assignee">
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    Assignee
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 1.5, md: 1.5 }} key="header-status">
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    Status
                                </Typography>
                            </Grid>
                        </Grid>

                        {/* Items - Map over category.items  */}
                        {category.items.map((deficiency, itemIndex) => {
                            // Get priority styling information
                            const priorityInfo = getPriorityInfo(deficiency);
                            const isPriority = isHighPriority(deficiency);

                            return (
                                <Grid
                                    container
                                    size={{ xs: 12, sm: 12, md: 12 }}
                                    key={getDeficiencyId(deficiency)}
                                >
                                    <Box
                                        onClick={(event) =>
                                            handleDeficiencyClick(deficiency, event)
                                        }
                                        sx={{
                                            position: 'relative',
                                            width: '100%',
                                            px: { xs: 2, sm: 2 },
                                            py: { xs: 2.5, sm: 2 },
                                            // Priority styling overrides selection styling when not selected
                                            border: selectedItems.has(getDeficiencyId(deficiency))
                                                ? '1px solid'
                                                : priorityInfo
                                                  ? `${priorityInfo.borderWidth} solid`
                                                  : 'none',
                                            borderColor: selectedItems.has(
                                                getDeficiencyId(deficiency)
                                            )
                                                ? theme.palette.primary.main
                                                : priorityInfo?.borderColor || 'transparent',
                                            borderRadius: { xs: 0, sm: 2 },
                                            backgroundColor: selectedItems.has(
                                                getDeficiencyId(deficiency)
                                            )
                                                ? `${theme.palette.primary.main}10`
                                                : priorityInfo?.backgroundColor || 'white',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: selectedItems.has(
                                                    getDeficiencyId(deficiency)
                                                )
                                                    ? `${theme.palette.primary.main}20`
                                                    : priorityInfo?.backgroundColor ||
                                                      theme.palette.grey[50],
                                                cursor: 'pointer',
                                                boxShadow: isPriority
                                                    ? '0 2px 8px rgba(0,0,0,0.1)'
                                                    : 'none',
                                            },
                                            borderLeft: {
                                                xs: 'none',
                                                sm: selectedItems.has(getDeficiencyId(deficiency))
                                                    ? '1px solid'
                                                    : priorityInfo
                                                      ? `${priorityInfo.borderWidth} solid`
                                                      : 'none',
                                            },
                                            borderRight: {
                                                xs: 'none',
                                                sm: selectedItems.has(getDeficiencyId(deficiency))
                                                    ? '1px solid'
                                                    : priorityInfo
                                                      ? `${priorityInfo.borderWidth} solid`
                                                      : 'none',
                                            },
                                            borderTop: {
                                                xs: itemIndex !== 0 ? 'none' : 'none',
                                                sm: selectedItems.has(getDeficiencyId(deficiency))
                                                    ? '1px solid'
                                                    : priorityInfo
                                                      ? `${priorityInfo.borderWidth} solid`
                                                      : 'none',
                                            },
                                            // Add subtle bottom divider instead of border
                                            borderBottom: {
                                                xs:
                                                    itemIndex !== category.items.length - 1
                                                        ? `1px solid ${theme.palette.grey[100]}`
                                                        : 'none',
                                                sm: selectedItems.has(getDeficiencyId(deficiency))
                                                    ? '1px solid'
                                                    : priorityInfo
                                                      ? `${priorityInfo.borderWidth} solid`
                                                      : 'none',
                                            },
                                            mb: { xs: 1, sm: 1 },
                                            minHeight: { xs: '156px', sm: '96px' }, // Set consistent height
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {/* Notification banner at the top-right - only render if there are notifications */}
                                        <Box
                                            className="notification-banner-container"
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                zIndex: 2,
                                                pointerEvents: 'none',
                                            }}
                                        >
                                            <NotificationBanner
                                                deficiencyId={getDeficiencyId(deficiency)}
                                                dueDateState={deficiency.dueDateState}
                                            />
                                        </Box>

                                        {/* Mobile Layout */}
                                        <Grid
                                            container
                                            sx={{
                                                display: { xs: 'flex', sm: 'none' },
                                                pl: 3, // Reduced padding since emojis are smaller without circles
                                                pt: 1.5, // Added top padding for indicator space
                                            }}
                                            spacing={1}
                                        >
                                            {/* Title at the top */}
                                            <Grid size={12} sx={{ mb: 1.5 }}>
                                                <Box sx={{ width: '100%' }}>
                                                    {/* Title with full width */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            gap: 1,
                                                            mb: 1,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: '1.05rem',
                                                                lineHeight: 1.4,
                                                                color: theme.palette.text.primary,
                                                                flex: 1,
                                                                textAlign: 'left',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 3,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            {deficiency.title}
                                                        </Typography>
                                                    </Box>

                                                    {/* Status below title */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'flex-start',
                                                            mt: 0.5,
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        {isInternalManager &&
                                                        (deficiency.status === 'Submitted' ||
                                                            deficiency.status === 'Accepted' ||
                                                            deficiency.status ===
                                                                'Work Completed') ? (
                                                            <QuickActionButton
                                                                status={deficiency.status}
                                                                deficiencyId={getDeficiencyId(
                                                                    deficiency
                                                                )}
                                                                onAction={(action, data) => {
                                                                    if (action === 'accept') {
                                                                        onStatusChange(
                                                                            getDeficiencyId(
                                                                                deficiency
                                                                            ),
                                                                            'Accepted'
                                                                        );
                                                                    } else if (
                                                                        action === 'reject_terminal'
                                                                    ) {
                                                                        // Terminal rejection from SUBMITTED
                                                                        onTerminalRejection(
                                                                            getDeficiencyId(
                                                                                deficiency
                                                                            )
                                                                        );
                                                                    } else if (
                                                                        action === 'reject_work'
                                                                    ) {
                                                                        // Work rejection/bounce-back from WORK_COMPLETED
                                                                        onWorkRejection(
                                                                            getDeficiencyId(
                                                                                deficiency
                                                                            )
                                                                        );
                                                                    } else if (
                                                                        action === 'sign-off'
                                                                    ) {
                                                                        onStatusChange(
                                                                            getDeficiencyId(
                                                                                deficiency
                                                                            ),
                                                                            'Internally Signed Off'
                                                                        );
                                                                    } else if (
                                                                        action === 'assign-self'
                                                                    ) {
                                                                        onAssign(
                                                                            getDeficiencyId(
                                                                                deficiency
                                                                            )
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        ) : (
                                                            <StatusChip
                                                                status={deficiency.status}
                                                                role={role}
                                                                showDescription={isInternalManager}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Grid>

                                            {/* Images */}
                                            <Grid size={12}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 2,
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    {/* Before image always in its container */}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.palette.text.secondary,
                                                                fontWeight: 500,
                                                                mb: 0.5,
                                                                display: 'block',
                                                                textAlign: 'left',
                                                            }}
                                                        >
                                                            Before
                                                        </Typography>
                                                        {deficiency.beforeImages?.[0] ? (
                                                            <Box
                                                                component="img"
                                                                src={getImageUrl(
                                                                    deficiency.beforeImages[0]
                                                                        .imageUrl
                                                                )}
                                                                alt="Before"
                                                                sx={{
                                                                    width: '100%',
                                                                    height: 120,
                                                                    borderRadius: 1,
                                                                    objectFit: 'cover',
                                                                    backgroundColor: '#f5f5f5',
                                                                    boxShadow:
                                                                        '0 1px 2px rgba(0,0,0,0.08)',
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    height: 120,
                                                                    borderRadius: 1,
                                                                    backgroundColor: '#f5f5f5',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    No image
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    {/* After image container - always shown */}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.palette.text.secondary,
                                                                fontWeight: 500,
                                                                mb: 0.5,
                                                                display: 'block',
                                                                textAlign: 'left',
                                                            }}
                                                        >
                                                            After
                                                        </Typography>
                                                        {deficiency.afterImages?.[0] ? (
                                                            <Box
                                                                component="img"
                                                                src={getImageUrl(
                                                                    deficiency.afterImages[0]
                                                                        .imageUrl
                                                                )}
                                                                alt="After"
                                                                sx={{
                                                                    width: '100%',
                                                                    height: 120,
                                                                    borderRadius: 1,
                                                                    objectFit: 'cover',
                                                                    backgroundColor: '#f5f5f5',
                                                                    boxShadow:
                                                                        '0 1px 2px rgba(0,0,0,0.08)',
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    height: 120,
                                                                    borderRadius: 1,
                                                                    backgroundColor: '#f5f5f5',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    No image
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Grid>

                                            {/* Assignee at the bottom */}
                                            <Grid size={12}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'center',
                                                        mt: 1.5,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: theme.palette.text.secondary,
                                                            mr: 1,
                                                        }}
                                                    >
                                                        Assignee:
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 400,
                                                            color:
                                                                getAssignedBusinessName(
                                                                    deficiency
                                                                ) === 'Unassigned'
                                                                    ? theme.palette.text.secondary
                                                                    : theme.palette.text.primary,
                                                        }}
                                                    >
                                                        {getAssignedBusinessName(deficiency)}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        {/* Desktop/Tablet Layout */}
                                        <Grid
                                            container
                                            alignItems="center"
                                            sx={{
                                                display: { xs: 'none', sm: 'flex' },
                                                pl: 3, // Reduced padding since emojis are smaller without circles
                                                pt: 1, // Added top padding for indicator space
                                            }}
                                        >
                                            <Grid
                                                size={{ xs: 12, sm: 2, md: 2 }}
                                                key={`${getDeficiencyId(deficiency)}-before`}
                                            >
                                                {deficiency.beforeImages?.[0] && (
                                                    <Box
                                                        component="img"
                                                        src={getImageUrl(
                                                            deficiency.beforeImages[0].imageUrl
                                                        )}
                                                        alt="Before"
                                                        sx={{
                                                            width: { xs: '100%', sm: 80 },
                                                            height: { xs: 150, sm: 80 },
                                                            borderRadius: 1,
                                                            objectFit: 'cover',
                                                            backgroundColor: '#f9f9f9',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                        }}
                                                    />
                                                )}
                                            </Grid>
                                            <Grid
                                                size={{ xs: 12, sm: 2, md: 2 }}
                                                key={`${getDeficiencyId(deficiency)}-after`}
                                            >
                                                {deficiency.afterImages?.[0] && (
                                                    <Box
                                                        component="img"
                                                        src={getImageUrl(
                                                            deficiency.afterImages[0].imageUrl
                                                        )}
                                                        alt="After"
                                                        sx={{
                                                            width: { xs: '100%', sm: 80 },
                                                            height: { xs: 150, sm: 80 },
                                                            borderRadius: 1,
                                                            objectFit: 'cover',
                                                            backgroundColor: '#f9f9f9',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                        }}
                                                    />
                                                )}
                                            </Grid>
                                            <Grid
                                                size={{ xs: 12, sm: 3.5, md: 3.5 }}
                                                sx={{ textAlign: 'left' }}
                                                key={`${getDeficiencyId(deficiency)}-title`}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{ fontWeight: 500 }}
                                                        >
                                                            {deficiency.title}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid
                                                size={{ xs: 6, sm: 1.5, md: 1.5 }}
                                                key={`${getDeficiencyId(deficiency)}-location`}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    {deficiency.roiName || 'N/A'}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                size={{ xs: 6, sm: 1.5, md: 1.5 }}
                                                key={`${getDeficiencyId(deficiency)}-assignee`}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    {getAssignedBusinessName(deficiency)}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                size={{ xs: 6, sm: 1.5, md: 1.5 }}
                                                key={`${getDeficiencyId(deficiency)}-status`}
                                            >
                                                {isInternalManager &&
                                                (deficiency.status === 'Submitted' ||
                                                    deficiency.status === 'Accepted' ||
                                                    deficiency.status === 'Work Completed') ? (
                                                    <QuickActionButton
                                                        status={deficiency.status}
                                                        deficiencyId={getDeficiencyId(deficiency)}
                                                        onAction={(action, data) => {
                                                            if (action === 'accept') {
                                                                onStatusChange(
                                                                    getDeficiencyId(deficiency),
                                                                    'Accepted'
                                                                );
                                                            } else if (
                                                                action === 'reject_terminal'
                                                            ) {
                                                                // Terminal rejection from SUBMITTED
                                                                onTerminalRejection(
                                                                    getDeficiencyId(deficiency)
                                                                );
                                                            } else if (action === 'reject_work') {
                                                                // Work rejection/bounce-back from WORK_COMPLETED
                                                                onWorkRejection(
                                                                    getDeficiencyId(deficiency)
                                                                );
                                                            } else if (action === 'sign-off') {
                                                                onStatusChange(
                                                                    getDeficiencyId(deficiency),
                                                                    'Internally Signed Off'
                                                                );
                                                            } else if (action === 'assign-self') {
                                                                onAssign(
                                                                    getDeficiencyId(deficiency)
                                                                );
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <StatusChip
                                                        status={deficiency.status}
                                                        role={role}
                                                        showDescription={isInternalManager}
                                                    />
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Paper>
                </Box>
            ))}
        </Box>
    );
};

export default DeficiencyList;
